import React, { useState, useEffect, useRef } from 'react';
import { Form } from 'react-bootstrap';
import MenuService from '../../../services/MenuService';

const ParentMenuSearchSelect = ({
    value = '',
    onChange,
    placeholder = "Search and select parent menu...",
    name = "parentId",
    label = "Parent Menu",
    required = false,
    disabled = false,
    error = '',
    className = '',
    showLabel = true,
    allowClear = true,
    size = 'md' // sm, md, lg
}) => {
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [filteredOptions, setFilteredOptions] = useState([]);
    const [selectedOption, setSelectedOption] = useState(null);
    const [dropdownPosition, setDropdownPosition] = useState('bottom');
    
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);

    // Load initial parent menus
    useEffect(() => {
        loadParentMenus();
    }, []);

    // Handle value prop changes
    useEffect(() => {
        const selected = options.find(opt => opt.id === value) || null;
        setSelectedOption(selected);
        setSearchTerm(selected ? `${selected.title}${selected.childrenCount > 0 ? ` (${selected.childrenCount} children)` : ''}` : '');
    }, [value, options]);

    // Filter options based on search
    useEffect(() => {
        if (!searchTerm) {
            setFilteredOptions(options);
        } else {
            const filtered = options.filter(option =>
                option.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (option.description && option.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (option.to && option.to.toLowerCase().includes(searchTerm.toLowerCase()))
            );
            setFilteredOptions(filtered);
        }
    }, [searchTerm, options]);

    // Handle click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                // Reset search term if no selection
                if (!selectedOption) {
                    setSearchTerm('');
                } else if (selectedOption) {
                    setSearchTerm(`${selectedOption.title}${selectedOption.childrenCount > 0 ? ` (${selectedOption.childrenCount} children)` : ''}`);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [selectedOption]);

    const loadParentMenus = async () => {
        try {
            setLoading(true);
            const response = await MenuService.getParentMenus();
            if (response.success && response.data) {
                setOptions(response.data);
            }
        } catch (error) {
            console.error('Error loading parent menus:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const term = e.target.value;
        setSearchTerm(term);
        setIsOpen(true);
        
        // Calculate dropdown position
        if (inputRef.current) {
            const rect = inputRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            
            if (spaceBelow < 300 && spaceAbove > spaceBelow) {
                setDropdownPosition('top');
            } else {
                setDropdownPosition('bottom');
            }
        }
    };

    const handleInputFocus = () => {
        setIsOpen(true);
        
        // Calculate dropdown position
        if (inputRef.current) {
            const rect = inputRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            
            if (spaceBelow < 300 && spaceAbove > spaceBelow) {
                setDropdownPosition('top');
            } else {
                setDropdownPosition('bottom');
            }
        }
    };

    const handleSelectOption = (option) => {
        setSelectedOption(option);
        setSearchTerm(`${option.title}${option.childrenCount > 0 ? ` (${option.childrenCount} children)` : ''}`);
        setIsOpen(false);
        
        if (onChange) {
            onChange({
                target: {
                    name,
                    value: option.id
                }
            });
        }
    };

    const handleClear = (e) => {
        e.stopPropagation();
        setSelectedOption(null);
        setSearchTerm('');
        setFilteredOptions(options);
        
        if (onChange) {
            onChange({
                target: {
                    name,
                    value: null
                }
            });
        }
    };

    const getSizeClass = () => {
        switch (size) {
            case 'sm': return 'form-control-sm';
            case 'lg': return 'form-control-lg';
            default: return '';
        }
    };

    const dropdownStyle = {
        position: 'absolute',
        ...(dropdownPosition === 'top' 
            ? { bottom: '100%', marginBottom: '2px' }
            : { top: '100%', marginTop: '2px' }
        ),
        left: 0,
        right: 0,
        zIndex: 1050,
        maxHeight: '300px',
        overflowY: 'auto',
        backgroundColor: '#fff',
        border: '1px solid #dee2e6',
        borderRadius: '0.375rem',
        boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)'
    };

    return (
        <Form.Group className={`mb-3 ${className}`}>
            {showLabel && (
                <Form.Label>
                    {label}
                    {required && <span className="text-danger ms-1">*</span>}
                    {!required && <span className="text-muted ms-1">(Optional)</span>}
                </Form.Label>
            )}
            
            <div ref={dropdownRef} style={{ position: 'relative' }}>
                <div className="position-relative">
                    <Form.Control
                        ref={inputRef}
                        type="text"
                        value={searchTerm}
                        onChange={handleInputChange}
                        onFocus={handleInputFocus}
                        placeholder={placeholder}
                        disabled={disabled || loading}
                        className={`${getSizeClass()} ${error ? 'is-invalid' : ''}`}
                        autoComplete="off"
                    />
                    
                    <div className="position-absolute top-50 end-0 translate-middle-y pe-2" style={{ pointerEvents: 'none' }}>
                        {loading && (
                            <div className="spinner-border spinner-border-sm text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        )}
                        {!loading && allowClear && selectedOption && !disabled && (
                            <button
                                type="button"
                                className="btn btn-sm p-0 border-0 bg-transparent"
                                onClick={handleClear}
                                style={{ pointerEvents: 'auto', marginRight: '8px' }}
                                title="Clear selection"
                            >
                                <i className="fa fa-times text-muted"></i>
                            </button>
                        )}
                        {!loading && (
                            <i className="fa fa-chevron-down text-muted" style={{ fontSize: '0.75rem' }}></i>
                        )}
                    </div>
                </div>

                {/* Dropdown List */}
                {isOpen && !disabled && (
                    <div style={dropdownStyle}>
                        {filteredOptions.length === 0 ? (
                            <div className="p-3 text-center text-muted">
                                {loading ? 'Loading...' : 'No parent menus found'}
                            </div>
                        ) : (
                            <div>
                                {filteredOptions.map((option) => (
                                    <div
                                        key={option.id}
                                        onClick={() => handleSelectOption(option)}
                                        style={{
                                            padding: '8px 12px',
                                            cursor: 'pointer',
                                            backgroundColor: selectedOption?.id === option.id ? '#f8f9fa' : '#fff',
                                            borderBottom: '1px solid #f0f0f0'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                        onMouseLeave={(e) => {
                                            if (selectedOption?.id !== option.id) {
                                                e.currentTarget.style.backgroundColor = '#fff';
                                            }
                                        }}
                                    >
                                        <div className="d-flex align-items-center">
                                            <div className="flex-grow-1">
                                                <div className="d-flex align-items-center">
                                                    {option.icon && (
                                                        <i className={`las la-${option.icon} me-2`} style={{ fontSize: '1.2rem' }}></i>
                                                    )}
                                                    <strong>{option.title}</strong>
                                                    {option.childrenCount > 0 && (
                                                        <span className="badge bg-primary ms-2" style={{ fontSize: '0.7rem' }}>
                                                            {option.childrenCount} children
                                                        </span>
                                                    )}
                                                    {option.status && (
                                                        <span className={`badge ms-2 ${option.status === 'Active' ? 'bg-success' : 'bg-secondary'}`} style={{ fontSize: '0.7rem' }}>
                                                            {option.status}
                                                        </span>
                                                    )}
                                                </div>
                                                {option.to && (
                                                    <div className="text-muted small mt-1">
                                                        <i className="fa fa-link me-1"></i>
                                                        {option.to}
                                                    </div>
                                                )}
                                                {option.description && (
                                                    <div className="text-muted small">
                                                        {option.description}
                                                    </div>
                                                )}
                                            </div>
                                            {selectedOption?.id === option.id && (
                                                <i className="fa fa-check text-primary ms-2"></i>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
            
            {error && (
                <Form.Control.Feedback type="invalid" className="d-block">
                    {error}
                </Form.Control.Feedback>
            )}
            
            {!error && !required && !selectedOption && (
                <Form.Text className="text-muted">
                    Leave empty to create a top-level menu item
                </Form.Text>
            )}
        </Form.Group>
    );
};

export default ParentMenuSearchSelect;
